import React, { useState, useEffect, useCallback } from 'react';
import { useConfirmDialog } from './components/ui/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============ COMPONENTE PRINCIPALE ============

export function FinanzeClub({ token, onRefresh }) {
  const { showConfirm } = useConfirmDialog();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [movimenti, setMovimenti] = useState([]);
  const [quote, setQuote] = useState([]);
  const [categorie, setCategorie] = useState({ entrata: [], uscita: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [isAdmin, setIsAdmin] = useState(true); // Default to true for backward compatibility
  
  // Filtri
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroAnno, setFiltroAnno] = useState(new Date().getFullYear());
  const [anniDisponibili, setAnniDisponibili] = useState([new Date().getFullYear()]);
  
  // Modal
  const [showNuovoMovimento, setShowNuovoMovimento] = useState(false);
  const [showNuovaQuota, setShowNuovaQuota] = useState(false);
  const [showQuoteBatch, setShowQuoteBatch] = useState(false);
  const [showPagaQuota, setShowPagaQuota] = useState(null);
  const [showBilancio, setShowBilancio] = useState(false);
  const [editingMovimento, setEditingMovimento] = useState(null);
  
  // Form
  const [movimentoForm, setMovimentoForm] = useState({
    tipo: 'entrata',
    categoria: '',
    importo: '',
    descrizione: '',
    data: new Date().toISOString().split('T')[0],
    riferimento_socio_id: '',
    riferimento_socio_nome: '',
    note: ''
  });
  
  const [quotaForm, setQuotaForm] = useState({
    socio_id: '',
    socio_nome: '',
    anno: new Date().getFullYear(),
    importo_dovuto: '',
    data_scadenza: ''
  });
  
  const [quoteBatchForm, setQuoteBatchForm] = useState({
    anno: new Date().getFullYear(),
    importo: '',
    data_scadenza: '',
    solo_attivi: true
  });
  
  const [pagamentoForm, setPagamentoForm] = useState({
    importo_pagato: '',
    metodo_pagamento: 'contanti',
    data_pagamento: new Date().toISOString().split('T')[0],
    note: ''
  });
  
  // Bilancio
  const [bilancio, setBilancio] = useState(null);
  const [annoBilancio, setAnnoBilancio] = useState(new Date().getFullYear());
  
  // Categorie personalizzate
  const [showGestioneCategorie, setShowGestioneCategorie] = useState(false);
  const [nuovaCategoriaForm, setNuovaCategoriaForm] = useState({ tipo: 'entrata', nome: '' });
  
  // Soci per select
  const [soci, setSoci] = useState([]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  const api = useCallback(async (endpoint, options = {}) => {
    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });
      return res;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  // ============ LOAD DATA ============

  const loadDashboard = useCallback(async () => {
    const r = await api('/api/clubs/finanze/dashboard');
    if (r?.ok) {
      const data = await r.json();
      setDashboard(data.dashboard);
    }
  }, [api]);

  const loadMovimenti = useCallback(async () => {
    let url = '/api/clubs/finanze/movimenti?limit=200';
    if (filtroTipo) url += `&tipo=${filtroTipo}`;
    
    const r = await api(url);
    if (r?.ok) {
      const data = await r.json();
      setMovimenti(data.movimenti || []);
    }
  }, [api, filtroTipo]);

  const loadQuote = useCallback(async () => {
    let url = `/api/clubs/finanze/quote?anno=${filtroAnno}`;
    const r = await api(url);
    if (r?.ok) {
      const data = await r.json();
      setQuote(data.quote || []);
      // Set admin flag from response
      if (data.is_admin !== undefined) {
        setIsAdmin(data.is_admin);
      }
    }
  }, [api, filtroAnno]);

  const loadCategorie = useCallback(async () => {
    const r = await api('/api/clubs/finanze/categorie');
    if (r?.ok) {
      const data = await r.json();
      setCategorie(data.categorie || { entrata: [], uscita: [] });
    }
  }, [api]);

  const loadAnniDisponibili = useCallback(async () => {
    const r = await api('/api/clubs/finanze/anni-disponibili');
    if (r?.ok) {
      const data = await r.json();
      setAnniDisponibili(data.anni || [new Date().getFullYear()]);
    }
  }, [api]);

  const loadSoci = useCallback(async () => {
    const r = await api('/api/clubs/anagrafica');
    if (r?.ok) {
      const data = await r.json();
      setSoci(data.soci || []);
    }
  }, [api]);

  const loadBilancio = useCallback(async (anno) => {
    const r = await api(`/api/clubs/finanze/bilancio/${anno}`);
    if (r?.ok) {
      const data = await r.json();
      setBilancio(data.bilancio);
    }
  }, [api]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadDashboard(),
        loadMovimenti(),
        loadQuote(),
        loadCategorie(),
        loadAnniDisponibili(),
        loadSoci()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [loadDashboard, loadMovimenti, loadQuote, loadCategorie, loadAnniDisponibili, loadSoci]);

  useEffect(() => {
    loadMovimenti();
  }, [filtroTipo, loadMovimenti]);

  useEffect(() => {
    loadQuote();
  }, [filtroAnno, loadQuote]);

  // ============ ACTIONS ============

  const saveMovimento = async () => {
    if (!movimentoForm.categoria || !movimentoForm.importo || !movimentoForm.descrizione) {
      showMsg('error', 'Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    const url = editingMovimento 
      ? `/api/clubs/finanze/movimenti/${editingMovimento.id}`
      : '/api/clubs/finanze/movimenti';
    const method = editingMovimento ? 'PUT' : 'POST';
    
    const r = await api(url, {
      method,
      body: JSON.stringify({
        ...movimentoForm,
        importo: parseFloat(movimentoForm.importo)
      })
    });

    if (r?.ok) {
      showMsg('success', editingMovimento ? 'Movimento aggiornato' : 'Movimento registrato');
      setShowNuovoMovimento(false);
      setEditingMovimento(null);
      resetMovimentoForm();
      loadDashboard();
      loadMovimenti();
      if (onRefresh) onRefresh();
    } else {
      const err = await r?.json();
      showMsg('error', err?.detail || 'Errore salvataggio');
    }
    setSaving(false);
  };

  const deleteMovimento = async (id) => {
    showConfirm({
      title: 'Elimina Movimento',
      message: 'Sei sicuro di voler eliminare questo movimento finanziario?',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/finanze/movimenti/${id}`, { method: 'DELETE' });
        if (r?.ok) {
          showMsg('success', 'Movimento eliminato');
          loadDashboard();
          loadMovimenti();
        }
      }
    });
  };

  const saveQuota = async () => {
    if (!quotaForm.socio_id || !quotaForm.importo_dovuto || !quotaForm.data_scadenza) {
      showMsg('error', 'Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    const r = await api('/api/clubs/finanze/quote', {
      method: 'POST',
      body: JSON.stringify({
        ...quotaForm,
        importo_dovuto: parseFloat(quotaForm.importo_dovuto)
      })
    });

    if (r?.ok) {
      showMsg('success', 'Quota creata');
      setShowNuovaQuota(false);
      resetQuotaForm();
      loadQuote();
      loadDashboard();
    } else {
      const err = await r?.json();
      showMsg('error', err?.detail || 'Errore creazione quota');
    }
    setSaving(false);
  };

  const createQuoteBatch = async () => {
    if (!quoteBatchForm.importo || !quoteBatchForm.data_scadenza) {
      showMsg('error', 'Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    const r = await api('/api/clubs/finanze/quote/batch', {
      method: 'POST',
      body: JSON.stringify({
        ...quoteBatchForm,
        importo: parseFloat(quoteBatchForm.importo)
      })
    });

    if (r?.ok) {
      const data = await r.json();
      showMsg('success', data.message);
      setShowQuoteBatch(false);
      loadQuote();
      loadDashboard();
    } else {
      const err = await r?.json();
      showMsg('error', err?.detail || 'Errore creazione quote');
    }
    setSaving(false);
  };

  const pagaQuota = async () => {
    if (!pagamentoForm.importo_pagato) {
      showMsg('error', 'Inserisci importo');
      return;
    }

    setSaving(true);
    const r = await api(`/api/clubs/finanze/quote/${showPagaQuota.id}/paga`, {
      method: 'POST',
      body: JSON.stringify({
        ...pagamentoForm,
        importo_pagato: parseFloat(pagamentoForm.importo_pagato)
      })
    });

    if (r?.ok) {
      showMsg('success', 'Pagamento registrato');
      setShowPagaQuota(null);
      resetPagamentoForm();
      loadQuote();
      loadDashboard();
      loadMovimenti();
    } else {
      const err = await r?.json();
      showMsg('error', err?.detail || 'Errore pagamento');
    }
    setSaving(false);
  };

  const deleteQuota = async (id) => {
    showConfirm({
      title: 'Elimina Quota',
      message: 'Sei sicuro di voler eliminare questa quota?',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/finanze/quote/${id}`, { method: 'DELETE' });
        if (r?.ok) {
          showMsg('success', 'Quota eliminata');
          loadQuote();
          loadDashboard();
        }
      }
    });
  };

  // ============ CATEGORIE PERSONALIZZATE ============

  const creaNuovaCategoria = async () => {
    if (!nuovaCategoriaForm.nome.trim()) {
      showMsg('error', 'Nome categoria obbligatorio');
      return;
    }
    
    setSaving(true);
    const r = await api('/api/clubs/finanze/categorie', {
      method: 'POST',
      body: JSON.stringify(nuovaCategoriaForm)
    });
    
    if (r?.ok) {
      showMsg('success', 'Categoria creata');
      setNuovaCategoriaForm({ tipo: 'entrata', nome: '' });
      loadCategorie();
    } else {
      const err = await r?.json();
      showMsg('error', err?.detail || 'Errore creazione categoria');
    }
    setSaving(false);
  };

  const eliminaCategoria = async (id, nome) => {
    showConfirm({
      title: 'Elimina Categoria',
      message: `Sei sicuro di voler eliminare la categoria "${nome}"?`,
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/finanze/categorie/${id}`, { method: 'DELETE' });
        if (r?.ok) {
          showMsg('success', 'Categoria eliminata');
          loadCategorie();
        }
      }
    });
  };

  // ============ HELPERS ============

  const resetMovimentoForm = () => {
    setMovimentoForm({
      tipo: 'entrata',
      categoria: '',
      importo: '',
      descrizione: '',
      data: new Date().toISOString().split('T')[0],
      riferimento_socio_id: '',
      riferimento_socio_nome: '',
      note: ''
    });
  };

  const resetQuotaForm = () => {
    setQuotaForm({
      socio_id: '',
      socio_nome: '',
      anno: new Date().getFullYear(),
      importo_dovuto: '',
      data_scadenza: ''
    });
  };

  const resetPagamentoForm = () => {
    setPagamentoForm({
      importo_pagato: '',
      metodo_pagamento: 'contanti',
      data_pagamento: new Date().toISOString().split('T')[0],
      note: ''
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getCategoriaLabel = (tipo, codice) => {
    const cats = categorie[tipo] || [];
    const cat = cats.find(c => c.codice === codice);
    return cat ? `${cat.icona || '📋'} ${cat.nome}` : codice;
  };

  const getStatoQuotaBadge = (stato) => {
    switch (stato) {
      case 'pagato':
      case 'pagata':  // Legacy support
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">✅ Pagato</span>;
      case 'parziale':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">💰 Parziale</span>;
      case 'arretrato':
      case 'scaduto':
      case 'scaduta':  // Legacy support
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">⚠️ Arretrato</span>;
      case 'in_attesa':
      case 'da_pagare':  // Legacy support
      default:
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">⏳ In attesa</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          💰 Gestione Finanze
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setShowNuovoMovimento(true); setEditingMovimento(null); resetMovimentoForm(); }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            data-testid="btn-nuovo-movimento"
          >
            <span>+ Movimento</span>
          </button>
          <button
            onClick={() => { setShowBilancio(true); loadBilancio(annoBilancio); }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            data-testid="btn-bilancio"
          >
            <span>📊 Bilancio</span>
          </button>
          <button
            onClick={() => setShowGestioneCategorie(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            data-testid="btn-gestione-categorie"
          >
            <span>🏷️ Categorie</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      {msg.text && (
        <div className={`px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'dashboard', label: '📊 Dashboard', icon: '📊' },
          { id: 'movimenti', label: '💸 Movimenti', icon: '💸' },
          { id: 'quote', label: '👥 Quote Soci', icon: '👥' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ============ DASHBOARD TAB ============ */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Saldo Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
              <p className="text-gray-500 text-sm">Saldo Totale</p>
              <p className={`text-2xl font-bold ${dashboard.saldo_attuale >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(dashboard.saldo_attuale)}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-green-500">
              <p className="text-gray-500 text-sm">Totale Entrate</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboard.entrate_totali)}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-red-500">
              <p className="text-gray-500 text-sm">Totale Uscite</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(dashboard.uscite_totali)}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-purple-500">
              <p className="text-gray-500 text-sm">Movimenti</p>
              <p className="text-2xl font-bold text-purple-600">{dashboard.num_movimenti_totali}</p>
            </div>
          </div>

          {/* Mese Corrente */}
          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="font-bold text-lg mb-4">📅 Mese Corrente</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-gray-500 text-sm">Entrate</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(dashboard.mese_corrente?.entrate)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Uscite</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(dashboard.mese_corrente?.uscite)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Saldo</p>
                <p className={`text-xl font-bold ${(dashboard.mese_corrente?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(dashboard.mese_corrente?.saldo)}
                </p>
              </div>
            </div>
          </div>

          {/* Quote Soci Status */}
          <div className="bg-white rounded-xl shadow p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">👥 Quote Soci {dashboard.quote_soci?.anno}</h3>
              <button
                onClick={() => setActiveTab('quote')}
                className="text-blue-600 hover:underline text-sm"
              >
                Gestisci →
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-800">{dashboard.quote_soci?.totale || 0}</p>
                <p className="text-xs text-gray-500">Totale</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{dashboard.quote_soci?.pagate || 0}</p>
                <p className="text-xs text-gray-500">Pagate</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{dashboard.quote_soci?.in_attesa || 0}</p>
                <p className="text-xs text-gray-500">In attesa</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{dashboard.quote_soci?.scadute || 0}</p>
                <p className="text-xs text-gray-500">Scadute</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t flex justify-between">
              <span className="text-gray-500">Da incassare:</span>
              <span className="font-bold text-orange-600">{formatCurrency(dashboard.quote_soci?.da_incassare)}</span>
            </div>
          </div>

          {/* Ultimi Movimenti */}
          <div className="bg-white rounded-xl shadow p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">📝 Ultimi Movimenti</h3>
              <button
                onClick={() => setActiveTab('movimenti')}
                className="text-blue-600 hover:underline text-sm"
              >
                Vedi tutti →
              </button>
            </div>
            {dashboard.ultimi_movimenti?.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nessun movimento registrato</p>
            ) : (
              <div className="space-y-2">
                {dashboard.ultimi_movimenti?.slice(0, 5).map(m => (
                  <div key={m.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                        m.tipo === 'entrata' ? 'bg-green-500' : 'bg-red-500'
                      }`}>
                        {m.tipo === 'entrata' ? '↓' : '↑'}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{m.descrizione}</p>
                        <p className="text-xs text-gray-400">{formatDate(m.data)}</p>
                      </div>
                    </div>
                    <span className={`font-bold ${m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                      {m.tipo === 'entrata' ? '+' : '-'}{formatCurrency(m.importo)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ MOVIMENTI TAB ============ */}
      {activeTab === 'movimenti' && (
        <div className="space-y-4">
          {/* Filtri */}
          <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-4 items-center">
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="px-3 py-2 border rounded-lg"
              data-testid="filtro-tipo"
            >
              <option value="">Tutti i tipi</option>
              <option value="entrata">Solo entrate</option>
              <option value="uscita">Solo uscite</option>
            </select>
            <button
              onClick={() => { setShowNuovoMovimento(true); setEditingMovimento(null); resetMovimentoForm(); }}
              className="ml-auto bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              + Nuovo Movimento
            </button>
          </div>

          {/* Lista Movimenti */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {movimenti.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">📝</div>
                <p>Nessun movimento registrato</p>
                <button
                  onClick={() => { setShowNuovoMovimento(true); resetMovimentoForm(); }}
                  className="mt-4 text-blue-600 hover:underline"
                >
                  Registra il primo movimento
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importo</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {movimenti.map(m => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{formatDate(m.data)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            m.tipo === 'entrata' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {m.tipo === 'entrata' ? '↓ Entrata' : '↑ Uscita'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{getCategoriaLabel(m.tipo, m.categoria)}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">{m.descrizione}</p>
                          {m.riferimento_socio_nome && (
                            <p className="text-xs text-gray-400">👤 {m.riferimento_socio_nome}</p>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${
                          m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {m.tipo === 'entrata' ? '+' : '-'}{formatCurrency(m.importo)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => {
                              setEditingMovimento(m);
                              setMovimentoForm({
                                tipo: m.tipo,
                                categoria: m.categoria,
                                importo: m.importo,
                                descrizione: m.descrizione,
                                data: m.data,
                                riferimento_socio_id: m.riferimento_socio_id || '',
                                riferimento_socio_nome: m.riferimento_socio_nome || '',
                                note: m.note || ''
                              });
                              setShowNuovoMovimento(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 px-2"
                            title="Modifica"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteMovimento(m.id)}
                            className="text-red-600 hover:text-red-800 px-2"
                            title="Elimina"
                            data-testid={`delete-movimento-${m.id}`}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ QUOTE TAB ============ */}
      {activeTab === 'quote' && (
        <div className="space-y-4">
          {/* Filtri e azioni */}
          <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-4 items-center">
            <select
              value={filtroAnno}
              onChange={(e) => setFiltroAnno(parseInt(e.target.value))}
              className="px-3 py-2 border rounded-lg"
              data-testid="filtro-anno-quote"
            >
              {anniDisponibili.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <div className="ml-auto flex gap-2">
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowQuoteBatch(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                    data-testid="btn-quote-batch"
                  >
                    👥 Genera Quote Batch
                  </button>
                  <button
                    onClick={() => { setShowNuovaQuota(true); resetQuotaForm(); }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    data-testid="btn-nuova-quota"
                  >
                    + Nuova Quota
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Lista Quote */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {quote.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">👥</div>
                <p>Nessuna quota per l&apos;anno {filtroAnno}</p>
                {isAdmin && (
                  <button
                    onClick={() => setShowQuoteBatch(true)}
                    className="mt-4 text-purple-600 hover:underline"
                  >
                    Genera quote per tutti i soci
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Socio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anno</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Dovuto</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pagato</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stato</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scadenza</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {quote.map(q => (
                      <tr key={q.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{q.socio_nome}</td>
                        <td className="px-4 py-3 text-sm">{q.anno}</td>
                        <td className="px-4 py-3 text-right font-medium">{formatCurrency(q.importo_dovuto)}</td>
                        <td className="px-4 py-3 text-right text-green-600">{formatCurrency(q.importo_pagato)}</td>
                        <td className="px-4 py-3 text-center">{getStatoQuotaBadge(q.stato)}</td>
                        <td className="px-4 py-3 text-sm">{formatDate(q.data_scadenza)}</td>
                        <td className="px-4 py-3 text-center">
                          {isAdmin && q.stato !== 'pagato' && q.stato !== 'pagata' && (
                            <button
                              onClick={() => {
                                setShowPagaQuota(q);
                                setPagamentoForm({
                                  importo_pagato: q.importo_dovuto - q.importo_pagato,
                                  metodo_pagamento: 'contanti',
                                  data_pagamento: new Date().toISOString().split('T')[0],
                                  note: ''
                                });
                              }}
                              className="text-green-600 hover:text-green-800 px-2"
                              title="Registra pagamento"
                              data-testid={`paga-quota-${q.id}`}
                            >
                              💳
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => deleteQuota(q.id)}
                              className="text-red-600 hover:text-red-800 px-2"
                              title="Elimina"
                            >
                              🗑️
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ MODALS ============ */}

      {/* Modal Nuovo/Modifica Movimento */}
      {showNuovoMovimento && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">
                {editingMovimento ? '✏️ Modifica Movimento' : '💰 Nuovo Movimento'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Tipo */}
              {!editingMovimento && (
                <div className="flex gap-4">
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer text-center transition-all ${
                    movimentoForm.tipo === 'entrata' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="tipo"
                      value="entrata"
                      checked={movimentoForm.tipo === 'entrata'}
                      onChange={(e) => setMovimentoForm({ ...movimentoForm, tipo: e.target.value, categoria: '' })}
                      className="hidden"
                    />
                    <span className="text-2xl block mb-1">↓</span>
                    <span className="font-medium text-green-700">Entrata</span>
                  </label>
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer text-center transition-all ${
                    movimentoForm.tipo === 'uscita' ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="tipo"
                      value="uscita"
                      checked={movimentoForm.tipo === 'uscita'}
                      onChange={(e) => setMovimentoForm({ ...movimentoForm, tipo: e.target.value, categoria: '' })}
                      className="hidden"
                    />
                    <span className="text-2xl block mb-1">↑</span>
                    <span className="font-medium text-red-700">Uscita</span>
                  </label>
                </div>
              )}

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium mb-1">Categoria *</label>
                <select
                  value={movimentoForm.categoria}
                  onChange={(e) => setMovimentoForm({ ...movimentoForm, categoria: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="movimento-categoria"
                >
                  <option value="">Seleziona categoria...</option>
                  {(categorie[movimentoForm.tipo] || []).map(cat => (
                    <option key={cat.codice} value={cat.codice}>{cat.icona} {cat.nome}</option>
                  ))}
                </select>
              </div>

              {/* Importo */}
              <div>
                <label className="block text-sm font-medium mb-1">Importo (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={movimentoForm.importo}
                  onChange={(e) => setMovimentoForm({ ...movimentoForm, importo: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="0.00"
                  data-testid="movimento-importo"
                />
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione *</label>
                <input
                  type="text"
                  value={movimentoForm.descrizione}
                  onChange={(e) => setMovimentoForm({ ...movimentoForm, descrizione: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Es: Affitto sala mese Gennaio"
                  data-testid="movimento-descrizione"
                />
              </div>

              {/* Data */}
              <div>
                <label className="block text-sm font-medium mb-1">Data *</label>
                <input
                  type="date"
                  value={movimentoForm.data}
                  onChange={(e) => setMovimentoForm({ ...movimentoForm, data: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="movimento-data"
                />
              </div>

              {/* Riferimento Socio (opzionale) */}
              <div>
                <label className="block text-sm font-medium mb-1">Collegato a socio (opzionale)</label>
                <select
                  value={movimentoForm.riferimento_socio_id}
                  onChange={(e) => {
                    const socio = soci.find(s => s.id === e.target.value);
                    setMovimentoForm({
                      ...movimentoForm,
                      riferimento_socio_id: e.target.value,
                      riferimento_socio_nome: socio?.nome || ''
                    });
                  }}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="">Nessuno</option>
                  {soci.map(s => (
                    <option key={s.id} value={s.id}>{s.nome}</option>
                  ))}
                </select>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium mb-1">Note (opzionale)</label>
                <textarea
                  value={movimentoForm.note}
                  onChange={(e) => setMovimentoForm({ ...movimentoForm, note: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg h-20"
                  placeholder="Note aggiuntive..."
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowNuovoMovimento(false); setEditingMovimento(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={saveMovimento}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                data-testid="movimento-submit"
              >
                {saving ? 'Salvataggio...' : (editingMovimento ? 'Aggiorna' : 'Registra')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nuova Quota */}
      {showNuovaQuota && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">👤 Nuova Quota Socio</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Socio *</label>
                <select
                  value={quotaForm.socio_id}
                  onChange={(e) => {
                    const socio = soci.find(s => s.id === e.target.value);
                    setQuotaForm({
                      ...quotaForm,
                      socio_id: e.target.value,
                      socio_nome: socio?.nome || ''
                    });
                  }}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="quota-socio"
                >
                  <option value="">Seleziona socio...</option>
                  {soci.map(s => (
                    <option key={s.id} value={s.id}>{s.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Anno *</label>
                <input
                  type="number"
                  value={quotaForm.anno}
                  onChange={(e) => setQuotaForm({ ...quotaForm, anno: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  min="2020"
                  max="2030"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Importo Dovuto (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={quotaForm.importo_dovuto}
                  onChange={(e) => setQuotaForm({ ...quotaForm, importo_dovuto: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="50.00"
                  data-testid="quota-importo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data Scadenza *</label>
                <input
                  type="date"
                  value={quotaForm.data_scadenza}
                  onChange={(e) => setQuotaForm({ ...quotaForm, data_scadenza: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="quota-scadenza"
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowNuovaQuota(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={saveQuota}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                {saving ? 'Creazione...' : 'Crea Quota'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Quote Batch */}
      {showQuoteBatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">👥 Genera Quote per Tutti i Soci</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 text-blue-700 p-4 rounded-lg text-sm">
                ℹ️ Verranno create quote per tutti i soci presenti in anagrafica. Quote già esistenti per lo stesso anno non saranno duplicate.
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Anno *</label>
                <input
                  type="number"
                  value={quoteBatchForm.anno}
                  onChange={(e) => setQuoteBatchForm({ ...quoteBatchForm, anno: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  min="2020"
                  max="2030"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Importo per Socio (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={quoteBatchForm.importo}
                  onChange={(e) => setQuoteBatchForm({ ...quoteBatchForm, importo: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="50.00"
                  data-testid="batch-importo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data Scadenza *</label>
                <input
                  type="date"
                  value={quoteBatchForm.data_scadenza}
                  onChange={(e) => setQuoteBatchForm({ ...quoteBatchForm, data_scadenza: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={quoteBatchForm.solo_attivi}
                  onChange={(e) => setQuoteBatchForm({ ...quoteBatchForm, solo_attivi: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Solo soci attivi</span>
              </label>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowQuoteBatch(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={createQuoteBatch}
                disabled={saving}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                {saving ? 'Generazione...' : 'Genera Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Paga Quota */}
      {showPagaQuota && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">💳 Registra Pagamento</h3>
              <p className="text-sm text-gray-500 mt-1">
                Quota {showPagaQuota.anno} - {showPagaQuota.socio_nome}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Importo dovuto:</span>
                  <span className="font-medium">{formatCurrency(showPagaQuota.importo_dovuto)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Già pagato:</span>
                  <span className="text-green-600">{formatCurrency(showPagaQuota.importo_pagato)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1 font-bold">
                  <span>Da pagare:</span>
                  <span className="text-orange-600">{formatCurrency(showPagaQuota.importo_dovuto - showPagaQuota.importo_pagato)}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Importo Pagamento (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={pagamentoForm.importo_pagato}
                  onChange={(e) => setPagamentoForm({ ...pagamentoForm, importo_pagato: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="pagamento-importo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Metodo Pagamento</label>
                <select
                  value={pagamentoForm.metodo_pagamento}
                  onChange={(e) => setPagamentoForm({ ...pagamentoForm, metodo_pagamento: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="contanti">💵 Contanti</option>
                  <option value="bonifico">🏦 Bonifico</option>
                  <option value="carta">💳 Carta</option>
                  <option value="altro">📝 Altro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data Pagamento</label>
                <input
                  type="date"
                  value={pagamentoForm.data_pagamento}
                  onChange={(e) => setPagamentoForm({ ...pagamentoForm, data_pagamento: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowPagaQuota(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={pagaQuota}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                {saving ? 'Registrazione...' : 'Registra Pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Bilancio Annuale */}
      {showBilancio && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">📊 Bilancio Annuale</h3>
              <select
                value={annoBilancio}
                onChange={(e) => {
                  setAnnoBilancio(parseInt(e.target.value));
                  loadBilancio(parseInt(e.target.value));
                }}
                className="px-3 py-2 border rounded-lg"
              >
                {anniDisponibili.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="p-6 space-y-6">
              {bilancio ? (
                <>
                  {/* Riepilogo */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="text-sm text-gray-500">Entrate</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(bilancio.totale_entrate)}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                      <p className="text-sm text-gray-500">Uscite</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(bilancio.totale_uscite)}</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${bilancio.saldo_finale >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                      <p className="text-sm text-gray-500">Saldo Finale</p>
                      <p className={`text-xl font-bold ${bilancio.saldo_finale >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                        {formatCurrency(bilancio.saldo_finale)}
                      </p>
                    </div>
                  </div>

                  {/* Per Categoria */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-bold text-green-700 mb-3">↓ Entrate per Categoria</h4>
                      {Object.entries(bilancio.per_categoria?.entrata || {}).length === 0 ? (
                        <p className="text-sm text-gray-500">Nessuna entrata</p>
                      ) : (
                        <ul className="space-y-2">
                          {Object.entries(bilancio.per_categoria?.entrata || {}).map(([cat, val]) => (
                            <li key={cat} className="flex justify-between text-sm">
                              <span>{getCategoriaLabel('entrata', cat)}</span>
                              <span className="font-medium text-green-600">{formatCurrency(val)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-bold text-red-700 mb-3">↑ Uscite per Categoria</h4>
                      {Object.entries(bilancio.per_categoria?.uscita || {}).length === 0 ? (
                        <p className="text-sm text-gray-500">Nessuna uscita</p>
                      ) : (
                        <ul className="space-y-2">
                          {Object.entries(bilancio.per_categoria?.uscita || {}).map(([cat, val]) => (
                            <li key={cat} className="flex justify-between text-sm">
                              <span>{getCategoriaLabel('uscita', cat)}</span>
                              <span className="font-medium text-red-600">{formatCurrency(val)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-bold text-purple-700 mb-3">👥 Quote Soci {annoBilancio}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-sm text-gray-500">Quote</p>
                        <p className="font-bold">{bilancio.quote?.num_quote}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Dovute</p>
                        <p className="font-bold">{formatCurrency(bilancio.quote?.totale_dovute)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Pagate</p>
                        <p className="font-bold text-green-600">{formatCurrency(bilancio.quote?.totale_pagate)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Da Incassare</p>
                        <p className="font-bold text-orange-600">{formatCurrency(bilancio.quote?.da_incassare)}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-center text-gray-400 text-sm">
                    Totale movimenti: {bilancio.num_movimenti}
                  </p>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                  Caricamento bilancio...
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowBilancio(false)}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestione Categorie Personalizzate */}
      {showGestioneCategorie && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold">🏷️ Gestione Categorie</h3>
                <button
                  onClick={() => setShowGestioneCategorie(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">Crea categorie personalizzate per i movimenti</p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Form Nuova Categoria */}
              <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                <h4 className="font-medium text-purple-700">+ Nuova Categoria</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tipo</label>
                    <select
                      value={nuovaCategoriaForm.tipo}
                      onChange={(e) => setNuovaCategoriaForm({ ...nuovaCategoriaForm, tipo: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="entrata">↓ Entrata</option>
                      <option value="uscita">↑ Uscita</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nome</label>
                    <input
                      type="text"
                      value={nuovaCategoriaForm.nome}
                      onChange={(e) => setNuovaCategoriaForm({ ...nuovaCategoriaForm, nome: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Es: Sponsor, Bar..."
                    />
                  </div>
                </div>
                <button
                  onClick={creaNuovaCategoria}
                  disabled={saving || !nuovaCategoriaForm.nome.trim()}
                  className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {saving ? 'Creazione...' : '+ Crea Categoria'}
                </button>
              </div>

              {/* Lista Categorie Entrata */}
              <div>
                <h4 className="font-medium text-green-700 mb-2">↓ Categorie Entrata</h4>
                <div className="space-y-2">
                  {(categorie.entrata || []).map(cat => (
                    <div key={cat.codice} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span>{cat.icona || '📋'}</span>
                        <span className="font-medium">{cat.nome}</span>
                        {cat.codice?.startsWith('custom_') && (
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Custom</span>
                        )}
                      </div>
                      {cat.codice?.startsWith('custom_') && (
                        <button
                          onClick={() => eliminaCategoria(cat.id, cat.nome)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Lista Categorie Uscita */}
              <div>
                <h4 className="font-medium text-red-700 mb-2">↑ Categorie Uscita</h4>
                <div className="space-y-2">
                  {(categorie.uscita || []).map(cat => (
                    <div key={cat.codice} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span>{cat.icona || '📋'}</span>
                        <span className="font-medium">{cat.nome}</span>
                        {cat.codice?.startsWith('custom_') && (
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Custom</span>
                        )}
                      </div>
                      {cat.codice?.startsWith('custom_') && (
                        <button
                          onClick={() => eliminaCategoria(cat.id, cat.nome)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowGestioneCategorie(false)}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinanzeClub;
